
import React, { useCallback, useEffect, useState } from 'react';
import { Fira_Sans } from 'next/font/google';
import '@styles/reset.scss';
import '@styles/adyenstyles.scss';
import '@styles/globals.scss';
import '@styles/nprogress-override.scss';
import 'nprogress/nprogress.css';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import '@styles/cookie-consent-override.scss';
import NextApp, { type AppContext, type AppInitialProps, type AppProps } from 'next/app';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import NProgress from 'nprogress';
import { PageWrapper } from '@components';
import {
  appLocalStorage, CartContext, CategoriesTreeContext, defaultSnackbarParams,
  registerSnackbarNotifier, SearchContext, SnackbarContext, SnackbarParams, UserContext
} from 'utils';
import { SmartSearchContext, ChatMessage } from '@utils/SmartSearchContext';
import { ConversationalProductsSearchResponse } from '@services/search/interfaces';
import { Customer } from '@commercetools/platform-sdk';
import { useServiceRef, useCookieConsent, useVisualViewportHeight } from 'hooks';
import { AuthorizationService, Category } from '@services';
import { Cart, CartService } from '@services/cart';
import { setRequestCookies } from '@services/auth';
import { ThemeProvider } from '@mui/material';
import { ThemeProvider as GridKitThemeProvider } from 'gd-design-library';
import { createTheme } from '@mui/material/styles';
import { Router, useRouter } from 'next/router';
import axios from 'axios';
import { TENANT_STORAGE_KEY } from '@constants/tenant';
import { Tenant, TenantContext } from '@utils/TenantContext';
import Cookies from 'js-cookie';
import { gridKitTheme } from 'theme';
import { McpService } from '@services/mcp';
import Script from 'next/script';
import Head from 'next/head';
import { TenantService } from '@services/tenant';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';

export const firaSans = Fira_Sans({
  weight: [ '400', '500', '600', '700' ],
  subsets: [ 'latin' ],
  display: 'swap'
});

Router.events.on('routeChangeStart', () => {
  NProgress.start();
});

Router.events.on('routeChangeComplete', () => {
  NProgress.done();
});

Router.events.on('routeChangeError', () => {
  NProgress.done();
});

const muiTheme = createTheme({
  typography: {
    fontFamily: firaSans.style.fontFamily
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          border: 'none'
        }
      }
    }
  }
});

const App: React.FC<AppProps> = ({ Component, pageProps, ...restProps }) => {
  const authServiceRef = useServiceRef(AuthorizationService);
  const cartServiceRef = useServiceRef(CartService);
  const tenantServiceRef = useServiceRef(TenantService);
  const mcpServiceRef = useServiceRef(McpService);
  const isCheckoutPage = restProps.router.pathname.includes('checkout');
  const isUcpPage = restProps.router.pathname.startsWith('/ucp');

  const [ user, setUser ] = useState<Customer | null>(null);
  const [ isUserLoading, setIsUserLoading ] = useState(true);
  const [ cart, setCart ] = useState<Cart | null>(null);
  const [ loading, setLoading ] = useState(false);
  const [ isCartDrawerOpen, setIsCartDrawerOpen ] = useState(false);
  const [ categories, setCategories ] = useState<Category[] | []>([]);
  const [ snackbarParams, setSnackbarParams ] = useState<SnackbarParams>(defaultSnackbarParams);
  const [ searchTerm, setSearchTerm ] = useState('');
  const [ chatHistory, setChatHistory ] = useState<ChatMessage[]>([]);
  const [ lastResponse, setLastResponse ] = useState<ConversationalProductsSearchResponse | null>(null);
  const [ originalQuery, setOriginalQuery ] = useState<string>('');
  const [ isSmartSearchOpen, setIsSmartSearchOpen ] = useState<boolean>(false);
  const [ tenants, setTenants ] = useState<Tenant[]>([]);
  const [ isMounted, setIsMounted ] = useState(false);
  const router = useRouter();
  useVisualViewportHeight();

  const toggleCartDrawer = useCallback((): void => {
    setIsCartDrawerOpen(isOpen => !isOpen);
    appLocalStorage.removeItem('isCartDrawerOpen');
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setIsCartDrawerOpen(!!appLocalStorage.getItem('isCartDrawerOpen'));
  }, []);

  useEffect(() => {
    if (!router.isReady || router.query['is-cart-drawer-open'] !== 'true') return;

    setIsCartDrawerOpen(true);
    const restQuery = { ...router.query };
    delete restQuery['is-cart-drawer-open'];
    void router.push({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
  }, [ router.isReady, router.query['is-cart-drawer-open'], router.pathname, router ]);

  useEffect((): (() => void) => {
    registerSnackbarNotifier((message: string): void => {
      setSnackbarParams({
        open: true,
        message,
        onClose: () => setSnackbarParams({ open: false, message: '', onClose: () => null }),
        severity: 'error'
      });
    });

    return () => registerSnackbarNotifier(null);
  }, [ setSnackbarParams ]);

  useCookieConsent(isMounted);

  // Defer McpService connection until after initial render
  useEffect(() => {
    if (!isMounted) return;
    mcpServiceRef.current.provideTools();
  }, [ isMounted, restProps.router ]);

  // This effect is used to add the tenant query parameter to the URL
  useEffect(() => {
    const currentTenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || 'null')?.key;

    if (currentTenantKey && !router.query.tenant && !router.pathname.startsWith('/ucp')) {
      const requiredParams = (router.pathname.match(/\[([^\]]+)\]/g) || []).map(match => match.slice(1, -1));
      const hasAllRequiredParams = requiredParams.every(param => router.query[param] != null);

      if (hasAllRequiredParams) {
        router.replace({
          pathname: router.pathname,
          query: {
            ...router.query,
            tenant: currentTenantKey
          }
        });
      }
    }
  }, [ router.pathname, router.query ]);

  useEffect(() => {
    (async (): Promise<void> => {
      authServiceRef.current.init();
      const customer = await authServiceRef.current.getSignedInCustomer();

      if (customer?.id) {
        setUser(customer);
      }

      const currentTenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || null)?.key;
      const fetchedCart = await cartServiceRef.current.getMyActiveCart(currentTenantKey);
      setCart(fetchedCart);
      setIsUserLoading(false);
    })();
  }, []);

  useEffect(() => {
    const fetchTenants = async (): Promise<void> => {
      try {
        setLoading(true);

        const tenantList = await tenantServiceRef.current.getTenants();
        setTenants(tenantList || []);

        const urlParamsTenantKey = new URLSearchParams(window.location.search).get('tenant');
        const urlParamsTenant = tenantList?.find(tenant => tenant.key === urlParamsTenantKey);
        const storedTenant = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || null);

        const defaultTenant = urlParamsTenant || storedTenant || tenantList?.[0];

        if (storedTenant?.key !== defaultTenant.key) {
          Cookies.set(TENANT_STORAGE_KEY, JSON.stringify(defaultTenant), {
            expires: 1
          });
          window.location.reload();
        }

        axios.defaults.headers.common['volcano-tenant-key'] = defaultTenant.key;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch tenants:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
      fetchTenants();
    }

    // Cookies.set(TENANT_STORAGE_KEY, JSON.stringify(DEFAULT_TENANT), {
    //   expires: 1
    // });
    // axios.defaults.headers.common['volcano-tenant-key'] = DEFAULT_TENANT.key;
  }, []);

  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;

  return (
    <>
      <Head>
        <meta
          name='description'
          content='Shop quality products online. Browse our selection of premium brands and exclusive offers.'
        />
        <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=1'></meta>
        {gtmId && (
          <>
            <link rel='preconnect' href='https://www.googletagmanager.com' />
            <link rel='dns-prefetch' href='https://www.googletagmanager.com' />
          </>
        )}
        <link
          rel='stylesheet'
          href='https://www.gstatic.com/ces-console/fast/chat-messenger/prod/v1.15/themes/chat-messenger-default.css'
        />
        <link
          rel='stylesheet'
          href='https://www.gstatic.com/ces-console/fast/chat-messenger/prod/v1.15/themes/chat-messenger-layout.css'
        />
      </Head>
      {/* CX Agent Studio messenger */}
      <Script
        defer
        strategy='lazyOnload'
        src='https://www.gstatic.com/ces-console/fast/chat-messenger/prod/v1.15/chat-messenger.js'
      />
      {/* Google Shopping Agent */}
      {!isUcpPage && isFeatureEnabled(FEATURES.GOOGLE_SHOPPING_AGENT) && (
        <Script async type='text/javascript' src='https://www.gstatic.com/cloud/ai/commerce_ai_ui/1.0.0/static.js' />
      )}
      {/* Google AI Prompts */}
      {isFeatureEnabled(FEATURES.GOOGLE_SHOPPING_AGENT) && (
        <Script async src='//www.gstatic.com/cloud/ai/commerce_ai_ui/google_ai_prompts/static.js' />
      )}
      {gtmId && (
        <Script
          id='gtm-consent-default'
          strategy='beforeInteractive'
          dangerouslySetInnerHTML={ {
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('consent', 'default', {
                analytics_storage: 'denied',
                ad_storage: 'denied'
              });
            `
          } }
        />
      )}
      {gtmId && (
        <Script
          id='gtm-script'
          strategy='lazyOnload'
          dangerouslySetInnerHTML={ {
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmId}');
            `
          } }
        />
      )}
      {!loading && (
        <StyledEngineProvider injectFirst>
          <GridKitThemeProvider initialTheme={ gridKitTheme }>
            <ThemeProvider theme={ muiTheme }>
              <UserContext.Provider value={ { user, setUser, isUserLoading, setIsUserLoading } }>
                <TenantContext.Provider value={ { tenants, setTenants, selectedTenant: null, setSelectedTenant: () => null } }>
                  <CartContext.Provider
                    value={ {
                      cart,
                      setCart,
                      isCartDrawerOpen,
                      setIsCartDrawerOpen,
                      toggleCartDrawer,
                    } }
                  >
                    <CategoriesTreeContext.Provider value={ { categories, setCategories } }>
                      <SearchContext.Provider value={ { searchTerm, setSearchTerm } }>
                        <SmartSearchContext.Provider
                          value={ {
                            chatHistory,
                            setChatHistory,
                            lastResponse,
                            setLastResponse,
                            originalQuery,
                            setOriginalQuery,
                            isSmartSearchOpen,
                            setIsSmartSearchOpen
                          } }
                        >
                          <SnackbarContext.Provider value={ { snackbarParams, setSnackbarParams } }>
                            <PageWrapper
                              hasHeader={ !isCheckoutPage && !isUcpPage }
                              hasFooter={ !isUcpPage }
                              showChatbotWidget={ !isUcpPage }
                            >
                              <Component { ...pageProps } />
                            </PageWrapper>
                          </SnackbarContext.Provider>
                        </SmartSearchContext.Provider>
                      </SearchContext.Provider>
                    </CategoriesTreeContext.Provider>
                  </CartContext.Provider>
                </TenantContext.Provider>
              </UserContext.Provider>
            </ThemeProvider>
          </GridKitThemeProvider>
        </StyledEngineProvider>
      )}
    </>
  );
};

const getInitialProps = async (appContext: AppContext): Promise<AppInitialProps> => {
  const cookieHeader = appContext.ctx.req?.headers?.cookie ?? '';
  setRequestCookies(cookieHeader);

  return NextApp.getInitialProps(appContext);
};

const AppWithGetInitialProps = Object.assign(App, { getInitialProps });

export default AppWithGetInitialProps;
